import { formatCount, formatInr } from '../../utils/formatInr';

const REASON_LABELS = {
  POS_SALE: 'POS sale',
  SALE: 'Delivery sale',
  ORDER_RESERVE: 'Delivery hold',
  ORDER_RELEASE: 'Hold released',
  ORDER_CANCEL: 'Order cancel',
  SUBS_DELIVER: 'Subscription drop',
  PURCHASE: 'Purchase',
  BULK_IMPORT: 'Bulk import',
  RETURN_RESTOCK: 'Return restock',
  ADJUST: 'Count adjust',
  CORRECTION: 'Count adjust',
  POS_VOID: 'POS void',
  POS_GAP_FILL: 'Gap fill',
  REPRICE: 'Price change',
  DAMAGE: 'Damage',
};

/** JSON numbers only. Strings are not coerced. */
export function asNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function reasonLabel(code) {
  const key = String(code || '').trim();
  if (!key) return '—';
  if (REASON_LABELS[key]) return REASON_LABELS[key];
  const words = key.toLowerCase().split('_').filter(Boolean);
  if (!words.length) return '—';
  return words
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export function formatInsightQty(value, soldBy) {
  const n = asNumber(value);
  if (n == null) return '—';
  if (String(soldBy || '').toUpperCase() === 'WEIGHT') {
    const rounded = Math.round(n * 1000) / 1000;
    const text = Number.isInteger(rounded)
      ? formatCount(rounded)
      : rounded.toLocaleString('en-IN', { maximumFractionDigits: 3 });
    return `${text} kg`;
  }
  return `${formatCount(n)} pcs`;
}

export function formatSignedQty(value, soldBy) {
  const n = asNumber(value);
  if (n == null) return '—';
  const body = formatInsightQty(Math.abs(n), soldBy);
  if (n > 0) return `+${body}`;
  if (n < 0) return `-${body}`;
  return body;
}

export function formatInsightMoney(value) {
  const n = asNumber(value);
  if (n == null) return '—';
  const digits = Number.isInteger(n) ? 0 : 2;
  return formatInr(n, { compact: false, digits });
}

const IST = 'Asia/Kolkata';

export function formatInsightAt(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Calendar date YYYY-MM-DD, not a timestamp. */
export function formatInsightDay(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return '—';
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Inclusive day count for YYYY-MM-DD. Null when the range is invalid. */
export function inclusiveDayCount(from, to) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from || '') || !/^\d{4}-\d{2}-\d{2}$/.test(to || '')) return null;
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.round((end - start) / 86400000) + 1;
}

/** null cover is “no sales”, never 0. */
export function coverDisplay(daysOfCover) {
  if (daysOfCover == null) return 'No sales in this period';
  const n = asNumber(daysOfCover);
  if (n == null) return 'No sales in this period';
  return String(n);
}

export function insightErrorCopy(err) {
  const code = err?.code;
  if (code === 'not_found') return { kind: 'catalog', message: 'Not in catalog.' };
  if (code === 'item_not_found' || err?.status === 404) {
    return { kind: 'removed', message: 'This product was removed.' };
  }
  if (code === 'forbidden' || err?.status === 403) {
    return { kind: 'forbidden', message: 'You do not have access to Product insight.' };
  }
  if (code === 'range_too_long') return { kind: 'range', message: 'Pick 90 days or fewer.' };
  if (code === 'range_too_heavy') {
    return { kind: 'range', message: 'Too many stock movements in this range. Shorten it.' };
  }
  if (code === 'invalid_cursor') return { kind: 'cursor', message: '' };
  if ([
    'invalid_period',
    'invalid_date',
    'invalid_range',
    'from_and_to_required',
    'invalid_channel',
    'invalid_limit',
  ].includes(code)) {
    return { kind: 'query', message: 'That date range or filter is not valid.' };
  }
  return { kind: 'other', message: 'Could not load product insight.' };
}
